import {
  type BrushConfig,
  createDrawingEngine,
  DEFAULT_BRUSHES,
  serializeDocument,
} from "@cookie/drawing";
import { createDraftStore, indexedDbBackend } from "@cookie/platform-web";
import {
  ArrowLeft,
  Download,
  Frame,
  Hand,
  LogOut,
  Pen,
  Redo2,
  RefreshCw,
  Undo2,
} from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  CanvasBoard,
  type CanvasMode,
  type SaveState,
} from "@/components/canvas/CanvasBoard";
import { Toolbar } from "@/components/canvas/Toolbar";
import { InviteDialog } from "@/components/studio/InviteDialog";
import { LoginQrDialog } from "@/components/studio/LoginQrDialog";
import { Onboarding } from "@/components/studio/Onboarding";
import { SpaceSetup } from "@/components/studio/SpaceSetup";
import { BrandWipe } from "@/components/studio/wipe/BrandWipe";
import { EXIT_WIPE } from "@/components/studio/wipe/wipe-math";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import { installDeepLinkListener } from "@/lib/deep-link";
import { saveLastSpaceId } from "@/lib/last-space";
import {
  installSessionMirror,
  restoreNativeSession,
  setNativeSpace,
  uploadFcmTokenIfPresent,
} from "@/lib/native-session";
import {
  RealtimeClient,
  reconcileSpace,
  setSyncSpace,
  syncEngine,
} from "@/lib/sync";
import {
  checkForUpdate,
  downloadRelease,
  ensureInstallPermission,
  installRelease,
  type ReleaseInfo,
} from "@/lib/updater";

const DRAFT_ID = "local";

// H7: Historial fuera del chunk inicial (solo se carga al abrir la pestaña).
const History = lazy(() =>
  import("@/components/studio/History").then((m) => ({ default: m.History })),
);

type Screen = "boot" | "onboarding" | "setup" | "studio";

const SAVE_LABEL: Record<SaveState, string> = {
  saved: "Borrador guardado",
  saving: "Guardando…",
  local: "Solo local",
};

export function App(): React.JSX.Element {
  const engine = useMemo(
    () =>
      createDrawingEngine({ width: 1024, height: 1024, background: "#FFFFFF" }),
    [],
  );
  const draftStore = useMemo(() => createDraftStore(indexedDbBackend()), []);
  const [screen, setScreen] = useState<Screen>("boot");
  const [spaceId, setSpaceId] = useState<string | null>(null);
  // Entrada al estudio con barrido de salida (135°): el estudio se
  // monta detrás del overlay y el wipe lo revela.
  const [entering, setEntering] = useState<string | null>(null);
  const [tab, setTab] = useState("historial");
  const [brush, setBrush] = useState<BrushConfig>(DEFAULT_BRUSHES.marker);
  const [canvasMode, setCanvasMode] = useState<CanvasMode>("draw");
  const [viewReset, setViewReset] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [confirmKind, setConfirmKind] = useState<"exit" | "logout" | null>(
    null,
  );
  const [logoutStep, setLogoutStep] = useState<1 | 2>(1);
  const [logoutText, setLogoutText] = useState("");
  const closeConfirm = (): void => {
    setConfirmKind(null);
    setLogoutStep(1);
    setLogoutText("");
  };
  // Actualización propia (H-updates): una lectura puntual por sesión.
  const [update, setUpdate] = useState<
    | { phase: "idle" }
    | {
        phase: "available" | "downloading" | "ready";
        release: ReleaseInfo;
        progress?: number;
      }
  >({ phase: "idle" });
  const updateCheckedRef = useRef(false);
  const [saveState, setSaveState] = useState<SaveState>("local");
  const [, setVersion] = useState(0);
  const [pending, setPending] = useState(0);
  const [failed, setFailed] = useState(0);
  const [historyKey, setHistoryKey] = useState(0);
  const [focusDrawing, setFocusDrawing] = useState<string | null>(null);
  const realtimeRef = useRef<RealtimeClient | null>(null);

  // Sin dispose al desmontar: el motor es singleton de la sesión (useMemo).
  // Con StrictMode el desmontaje simulado lo mataría (disposed=true) sin
  // recrearlo, dejando el lienzo mudo sin ningún error. La página al
  // cerrarse limpia sola.

  // Puente nativo (una vez): espejo de sesión para el worker + deep links.
  useEffect(() => {
    installSessionMirror();
    installDeepLinkListener((drawingId) => {
      setFocusDrawing(drawingId);
      setTab("historial");
    });
  }, []);

  useEffect(() => {
    // Android olvida sessionStorage al cerrar: restaura el espejo nativo
    // antes de decidir la pantalla, para seguir en la misma sala.
    void (async () => {
      await restoreNativeSession();
      if (!api.loggedIn) {
        setScreen("onboarding");
        return;
      }
      api
        .me()
        .then((me) => {
          if (me.pairSpaceId) {
            setSpaceId(me.pairSpaceId);
            setScreen("studio");
          } else {
            setScreen("setup");
          }
        })
        .catch(() => setScreen("onboarding"));
    })();
  }, []);

  // Último espacio conocido (rellena la recuperación, nunca memorizar UUID).
  useEffect(() => {
    if (spaceId) saveLastSpaceId(spaceId);
  }, [spaceId]);

  // Aviso de actualización: una sola lectura por sesión al entrar.
  useEffect(() => {
    if (screen !== "studio" || !spaceId || updateCheckedRef.current) return;
    updateCheckedRef.current = true;
    void checkForUpdate().then((res) => {
      if (res.available && res.release) {
        setUpdate({ phase: "available", release: res.release });
      }
    });
  }, [screen, spaceId]);

  const downloadAppUpdate = async (release: ReleaseInfo): Promise<void> => {
    setUpdate({ phase: "downloading", release, progress: 0 });
    try {
      await downloadRelease(release, (progress) =>
        setUpdate({ phase: "downloading", release, progress }),
      );
      setUpdate({ phase: "ready", release });
    } catch {
      toast.error("No se pudo descargar");
      setUpdate({ phase: "available", release });
    }
  };

  const installAppUpdate = async (release: ReleaseInfo): Promise<void> => {
    try {
      const granted = await ensureInstallPermission();
      if (!granted) {
        toast("Activa «apps desconocidas» para Cookie y vuelve a Instalar");
        return;
      }
      await installRelease(release.versionCode);
    } catch {
      toast.error("No se pudo instalar");
    }
  };

  // Sync + realtime vivos solo en studio.
  useEffect(() => {
    if (screen !== "studio" || !spaceId) return;
    setSyncSpace(spaceId);
    setNativeSpace(spaceId);
    void uploadFcmTokenIfPresent();
    const unsub = syncEngine.subscribe((s) => {
      setPending(s.pending);
      setFailed(s.failed);
    });
    void syncEngine.pump();
    const client = new RealtimeClient(spaceId, {
      onRemoteEvents: (events) => {
        setHistoryKey((k) => k + 1);
        if (events.some((e) => e.type === "drawing.created")) {
          toast("Nuevo dibujo recibido");
        }
      },
      onSnapshot: () => setHistoryKey((k) => k + 1),
    });
    realtimeRef.current = client;
    client.start();
    // Al entrar, reconciliar por si entraron dibujos con la app cerrada.
    void reconcileSpace(spaceId).then((r) => {
      if (r.snapshot || r.applied.length > 0) setHistoryKey((k) => k + 1);
    });
    const onOnline = (): void => {
      void syncEngine.pump();
      void client.refresh();
    };
    window.addEventListener("online", onOnline);
    const timer = window.setInterval(() => void syncEngine.pump(), 10_000);
    return () => {
      unsub();
      client.stop();
      realtimeRef.current = null;
      setNativeSpace(null);
      window.removeEventListener("online", onOnline);
      window.clearInterval(timer);
    };
  }, [screen, spaceId]);

  const publish = async (): Promise<void> => {
    try {
      const doc = engine.exportDocument();
      if (doc.strokes.length === 0) {
        toast.error("Dibuja algo primero");
        return;
      }
      // Outbox durable: sobrevive offline y reintentos sin duplicar
      // (misma mutationId como Idempotency-Key).
      await syncEngine.enqueue(
        serializeDocument(doc),
        doc.canvas.width,
        doc.canvas.height,
      );
      toast("En cola para publicar");
      const st = await syncEngine.pump();
      setHistoryKey((k) => k + 1);
      // A envía y sigue en el lienzo: el historial es solo de recibidos.
      if (st.pending === 0 && st.failed === 0) toast.success("Enviado");
    } catch {
      toast.error("No se pudo encolar");
    }
  };

  if (screen === "boot") {
    return (
      <main className="flex min-h-dvh items-center justify-center">
        <p className="font-pixel text-sm text-muted-foreground">Cargando…</p>
      </main>
    );
  }
  if (screen === "onboarding") {
    return (
      <Onboarding
        onDone={(id) => {
          if (id) {
            setSpaceId(id);
            setScreen("studio");
          } else {
            setScreen("setup");
          }
        }}
      />
    );
  }
  // Pantalla del estudio como nodo reutilizable: en la entrada normal
  // se renderiza sola; en el wipe de salida va recortada tras el
  // overlay mientras el setup sigue vivo debajo.
  const renderStudio = (id: string): React.JSX.Element => (
    <main className="min-h-dvh bg-background text-foreground">
      <Toaster />
      {tab === "lienzo" ? (
        <div className="relative flex h-dvh flex-col overflow-hidden">
          <div className="pointer-events-none absolute inset-x-0 top-4 z-20 flex justify-center px-4">
            <div className="pointer-events-none flex w-full max-w-3xl items-start justify-between gap-2">
              {confirmKind === "exit" ? (
                <div className="liquid-glass pointer-events-auto flex w-full items-center justify-between gap-2 rounded-full py-1.5 pr-1.5 pl-1.5 shadow-lg">
                  <span className="pl-2 text-sm font-medium">
                    ¿Seguro de salir?
                  </span>
                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="rounded-full"
                      onClick={closeConfirm}
                    >
                      No
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        closeConfirm();
                        setTab("historial");
                      }}
                      className="rounded-full bg-red-500 text-white shadow-sm hover:bg-red-500/90"
                    >
                      Sí, salir
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <Button
                    size="sm"
                    aria-label="Salir a recibidos"
                    title="Salir a recibidos"
                    onClick={() => setConfirmKind("exit")}
                    className="pointer-events-auto rounded-full bg-red-500 text-white shadow-lg hover:bg-red-500/90"
                  >
                    <ArrowLeft className="size-4" aria-hidden />
                    Salir
                  </Button>
                  <Button
                    size="sm"
                    className="pointer-events-auto rounded-full shadow-lg"
                    onClick={publish}
                  >
                    Enviar
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden px-4 pt-24 pb-36">
            <CanvasBoard
              engine={engine}
              brush={brush}
              draftStore={draftStore}
              draftId={DRAFT_ID}
              dashed
              mode={canvasMode}
              resetViewSignal={viewReset}
              onZoom={setZoom}
              onSaveState={setSaveState}
              onStrokesVersion={() => setVersion((v) => v + 1)}
            />
          </div>
          <div className="fixed bottom-8 left-1/2 z-20 flex w-max max-w-[calc(100vw-2rem)] -translate-x-1/2 justify-center">
            <div className="liquid-glass flex max-w-full flex-col items-center gap-2.5 rounded-3xl px-4 py-2 shadow-lg">
              <div
                className="flex items-center gap-1"
                role="toolbar"
                aria-label="Modo del lienzo"
              >
                <Button
                  variant={canvasMode === "pan" ? "default" : "ghost"}
                  size="sm"
                  aria-pressed={canvasMode === "pan"}
                  onClick={() => setCanvasMode("pan")}
                  className={
                    canvasMode === "pan"
                      ? "rounded-full"
                      : "rounded-full bg-white text-slate-800 shadow-sm hover:bg-white/90 hover:text-slate-800"
                  }
                >
                  <Hand className="size-4" aria-hidden />
                  Mover
                </Button>
                <Button
                  variant={canvasMode === "draw" ? "default" : "ghost"}
                  size="sm"
                  aria-pressed={canvasMode === "draw"}
                  onClick={() => setCanvasMode("draw")}
                  className={
                    canvasMode === "draw"
                      ? "rounded-full"
                      : "rounded-full bg-white text-slate-800 shadow-sm hover:bg-white/90 hover:text-slate-800"
                  }
                >
                  <Pen className="size-4" aria-hidden />
                  Pintar
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Deshacer"
                  disabled={!engine.canUndo()}
                  onClick={() => {
                    engine.undo();
                    setVersion((v) => v + 1);
                  }}
                  className="size-8 rounded-full"
                >
                  <Undo2 className="size-4" aria-hidden />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Rehacer"
                  disabled={!engine.canRedo()}
                  onClick={() => {
                    engine.redo();
                    setVersion((v) => v + 1);
                  }}
                  className="size-8 rounded-full"
                >
                  <Redo2 className="size-4" aria-hidden />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Centrar vista"
                  title="Centrar vista"
                  onClick={() => setViewReset((n) => n + 1)}
                  className="size-8 rounded-full"
                >
                  <Frame className="size-4" aria-hidden />
                </Button>
                <span
                  aria-live="polite"
                  title="Nivel de zoom"
                  className="w-12 shrink-0 text-center text-xs text-muted-foreground tabular-nums"
                >
                  {Math.round(((zoom - 1) / 7) * 100)}%
                </span>
              </div>
              <Toolbar
                brush={brush}
                onBrush={setBrush}
                canUndo={engine.canUndo()}
                canRedo={engine.canRedo()}
                onUndo={() => {
                  engine.undo();
                  setVersion((v) => v + 1);
                }}
                onRedo={() => {
                  engine.redo();
                  setVersion((v) => v + 1);
                }}
                hideHistoryActions
              />
            </div>
          </div>
          <div className="pointer-events-none absolute inset-x-0 bottom-1 z-10 flex items-center justify-center gap-2 text-[11px] text-muted-foreground">
            <span>
              {SAVE_LABEL[saveState]}
              {pending > 0 && ` · ${pending} pendiente`}
            </span>
            {failed > 0 && (
              <Button
                variant="link"
                size="sm"
                onClick={() => void syncEngine.retryAllFailed()}
                className="pointer-events-auto h-auto p-0 text-[11px]"
              >
                <RefreshCw className="size-3" aria-hidden /> Reintentar (
                {failed})
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="mx-auto flex max-w-3xl flex-col gap-5 px-4 py-8">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <LoginQrDialog
                label="Escanear QR"
                className="rounded-full bg-white text-slate-800 shadow-sm hover:bg-white/90 dark:bg-white dark:text-slate-800"
              />
              <InviteDialog
                spaceId={id}
                label="Invitar"
                className="rounded-full bg-white text-slate-800 shadow-sm hover:bg-white/90 dark:bg-white dark:text-slate-800"
              />
              <Button
                variant="ghost"
                size="sm"
                aria-label="Abandonar sala"
                onClick={() => setConfirmKind("logout")}
                className="rounded-full bg-red-500/15 text-red-600 hover:bg-red-500/25 hover:text-red-600 dark:text-red-400 dark:hover:text-red-400"
              >
                <LogOut className="size-4" aria-hidden />
                Abandonar sala
              </Button>
            </div>
            <Button className="rounded-full" onClick={() => setTab("lienzo")}>
              Dibujar
            </Button>
          </div>
          {update.phase !== "idle" && (
            <Card className="border-primary/40">
              <CardContent className="flex flex-col gap-2 pt-4">
                <p className="text-sm font-medium">
                  Hay actualización ({update.release.tag})
                </p>
                {update.release.notes && (
                  <p className="text-sm text-muted-foreground">
                    {update.release.notes}
                  </p>
                )}
                {update.phase === "available" && (
                  <div className="flex justify-end">
                    <Button
                      onClick={() => void downloadAppUpdate(update.release)}
                      className="rounded-full"
                    >
                      <Download className="size-4" aria-hidden />
                      Descargar
                    </Button>
                  </div>
                )}
                {update.phase === "downloading" && (
                  <output
                    className="flex items-center gap-2"
                    aria-label={`Descargando ${update.progress ?? 0}%`}
                  >
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary transition-[width]"
                        style={{ width: `${update.progress ?? 0}%` }}
                      />
                    </div>
                    <span className="w-10 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                      {update.progress ?? 0}%
                    </span>
                  </output>
                )}
                {update.phase === "ready" && (
                  <div className="flex justify-end">
                    <Button
                      onClick={() => void installAppUpdate(update.release)}
                      className="rounded-full"
                    >
                      Instalar
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          {confirmKind === "logout" && (
            <Dialog
              open
              onOpenChange={(open) => {
                if (!open) closeConfirm();
              }}
            >
              <DialogContent>
                {logoutStep === 2 ? (
                  <>
                    <DialogHeader>
                      <DialogTitle>Confirma que vas a salir</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                      Escribe «salir» para abandonar la sala en este
                      dispositivo.
                    </p>
                    <Input
                      value={logoutText}
                      onChange={(e) => setLogoutText(e.target.value)}
                      placeholder="salir"
                      autoComplete="off"
                      autoCapitalize="off"
                      spellCheck={false}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setLogoutStep(1);
                          setLogoutText("");
                        }}
                      >
                        Volver
                      </Button>
                      <Button
                        disabled={logoutText.trim().toLowerCase() !== "salir"}
                        onClick={() => {
                          realtimeRef.current?.stop();
                          setSyncSpace(null);
                          api.logout();
                          closeConfirm();
                          setScreen("onboarding");
                        }}
                        className="rounded-full bg-red-500 text-white shadow-sm hover:bg-red-500/90 disabled:opacity-50"
                      >
                        Abandonar sala
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <DialogHeader>
                      <DialogTitle>¿Seguro de salir?</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                      Se cerrará tu sesión en este dispositivo.
                    </p>
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" onClick={closeConfirm}>
                        Cancelar
                      </Button>
                      <Button
                        onClick={() => {
                          setLogoutStep(2);
                          setLogoutText("");
                        }}
                        className="rounded-full bg-red-500 text-white shadow-sm hover:bg-red-500/90"
                      >
                        Continuar
                      </Button>
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>
          )}
          <h2 className="font-pixel text-sm tracking-widest text-muted-foreground uppercase">
            Recibidos:
          </h2>
          <Suspense
            fallback={
              <p className="text-sm text-muted-foreground">
                Cargando recibidos…
              </p>
            }
          >
            <History key={historyKey} spaceId={id} focusId={focusDrawing} />
          </Suspense>
        </div>
      )}
    </main>
  );

  if (screen === "setup" || !spaceId) {
    return (
      <>
        <SpaceSetup
          onDone={(nextId) => {
            setSpaceId(nextId);
            setScreen("studio");
          }}
          onEnter={(nextId) => {
            // Movimiento reducido: entrar directo sin barrido.
            if (
              typeof window !== "undefined" &&
              typeof window.matchMedia === "function" &&
              window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ) {
              setSpaceId(nextId);
              setScreen("studio");
              return;
            }
            setEntering(nextId);
          }}
        />
        {entering && (
          <BrandWipe
            to={renderStudio(entering)}
            params={EXIT_WIPE}
            onDone={() => {
              setSpaceId(entering);
              setScreen("studio");
              setEntering(null);
            }}
          />
        )}
      </>
    );
  }
  return renderStudio(spaceId);
}
